import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Paper } from "@mantine/core";
import { gql } from "@/__generated__";
import Navbar from "@/components/layout/Navbar/Navbar";
import { ReferPatientProgressTracker } from "@/components/ReferPatientProgressTracker";
import { StrengthenReferral } from "../StrengthenReferral/StrengthenReferral";
import { SubmissionDetailsContent } from "./SubmissionDetailsContent";
import PageLoader from "@/components/PageLoader/PageLoader";
import { PageError } from "@/components/PageError/PageError";
import styles from "../StrengthenReferral/styles/tab-content.module.scss";

const GET_SUBMISSION_DETAILS = gql(`
  query GetSubmissionDetails($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      destination {
        name
        company
        fax
        address {
          addressLine1
          addressLine2
          city
          state
          zip
        }
      }
      ccDestinations {
        destination {
          name
          fax
          address {
            addressLine1
            addressLine2
            city
            state
            zip
          }
        }
      }
      sentFaxes {
        faxId
        faxNumber
        sentStatus
        initiatedTimestamp
        completedTimestamp
        errorDetails
      }
      sentLetters {
        letterId
        recipientName
        address {
          addressLine1
          addressLine2
          city
          state
          zip
        }
        initiatedTimestamp
        lobStatus
        expectedDeliveryDate
        currentExpectedDeliveryDate
        trackingNumber
      }
      status {
        referred
        appealSubmittedTimestamp
      }
      patientInsurance {
        providerName
      }
    }
  }
`);

export function SubmissionDetails() {
  const { appealId } = useParams<{ appealId: string }>();

  const { data, loading, error } = useQuery(GET_SUBMISSION_DETAILS, {
    variables: { appealId: appealId! },
    skip: !appealId,
    fetchPolicy: "no-cache",
  });

  if (!appealId) {
    return null;
  }

  if (loading) {
    return (
      <Navbar
        titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
      >
        <PageLoader />
      </Navbar>
    );
  }

  if (error || !data?.getAppealById) {
    return (
      <Navbar
        titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
      >
        <PageError />
      </Navbar>
    );
  }

  return (
    <Navbar
      titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
    >
      <StrengthenReferral
        appealId={appealId}
        activeTab="submission-details"
        tabContent={
          <Paper className={styles.container} p="md">
            <SubmissionDetailsContent appeal={data.getAppealById} />
          </Paper>
        }
      />
    </Navbar>
  );
}
